import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, JWTPayload } from '../types';
import { authAPI } from '../services/api';
import { clearStoredAuthMethod, setStoredAuthMethod } from '../utils/authMethod';
import { clearOidcDebugStorage } from '../utils/clearOidcDebugStorage';
import { isHttpOnlyAuthSessionEnabled, setAuthSessionUser } from '../utils/authSession';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  completeSsoLogin: (token: string, user: User, method: 'saml' | 'oidc') => void;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function syncSessionUser(user: User | null): void {
  if (user) {
    setAuthSessionUser({
      id: user.id,
      username: user.username || user.email,
      role: user.role,
      enterprise_id: user.enterprise_id,
    });
  } else {
    setAuthSessionUser(null);
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on app start
  useEffect(() => {
    const initAuth = async () => {
      if (isHttpOnlyAuthSessionEnabled()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        try {
          const isValid = await authAPI.validateToken();
          if (isValid) {
            const profile = await authAPI.getProfile();
            setUser(profile);
            syncSessionUser(profile);
          }
        } catch {
          /* not logged in */
        }
        setIsLoading(false);
        return;
      }

      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Validate token with backend
          const isValid = await authAPI.validateToken();
          if (isValid) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            syncSessionUser(JSON.parse(storedUser));
          } else {
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearStoredAuthMethod();
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          clearStoredAuthMethod();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Clear any existing cached data before login
      localStorage.removeItem('onboardingSubmissions');
      localStorage.removeItem('applications');
      localStorage.removeItem('ssoConfigurations');
      localStorage.removeItem('dashboardStats');
      clearOidcDebugStorage();
      
      const response = await authAPI.login({ username, password });

      // The API returns LoginResponse directly (not wrapped in ApiResponse)
      if (response && response.user && (response.token || isHttpOnlyAuthSessionEnabled())) {
        if (response.token) {
          setToken(response.token);
          localStorage.setItem('token', response.token);
        }
        setUser(response.user);
        syncSessionUser(response.user);
        if (!isHttpOnlyAuthSessionEnabled()) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        setStoredAuthMethod('password');
        return true;
      } else {
        console.error('Login failed: Invalid response format');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const completeSsoLogin = (newToken: string, newUser: User, method: 'saml' | 'oidc') => {
    if (isHttpOnlyAuthSessionEnabled()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
    } else if (newToken) {
      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
    }
    setUser(newUser);
    syncSessionUser(newUser);
    setStoredAuthMethod(method);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    setToken(null);
    syncSessionUser(null);
    clearOidcDebugStorage();
    
    // Clear cached app data (keep theme preferences)
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('onboardingSubmissions');
    localStorage.removeItem('applications');
    localStorage.removeItem('ssoConfigurations');
    localStorage.removeItem('dashboardStats');
    clearStoredAuthMethod();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      syncSessionUser(updatedUser);
      if (!isHttpOnlyAuthSessionEnabled()) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: isHttpOnlyAuthSessionEnabled() ? !!user : !!user && !!token,
    isLoading,
    login,
    completeSsoLogin,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
